/**
 * Manage Suppressions · Feb 2026 · R135g
 *
 * Workspace-wide email blocklist. Admins can add / remove entries,
 * search by email, and bulk-import unsubscribe lists via CSV drop.
 * Any address here is silently skipped by every bulk email blast
 * fired from the Leads screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Ban, Plus, Trash2, Upload, Search, RefreshCw } from "lucide-react";

const REASON_TONE = {
  manual:        "text-neutral-700 bg-neutral-100 border-neutral-300",
  unsubscribed:  "text-amber-700 bg-amber-100 border-amber-300",
  hard_bounce:   "text-rose-700 bg-rose-100 border-rose-300",
  complained:    "text-fuchsia-700 bg-fuchsia-100 border-fuchsia-300",
  blocked:       "text-neutral-700 bg-neutral-200 border-neutral-400",
};

export default function Suppressions() {
  const { activeWorkspaceId: workspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newReason, setNewReason] = useState("manual");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const q = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : "";
      const r = await api.get(`/workspaces/${workspaceId}/sales/suppressions${q}`);
      setRows(r.data?.rows || []);
      setCounts(r.data?.counts_by_reason || {});
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load suppressions")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [workspaceId]);

  const filtered = useMemo(() => rows, [rows]);

  const addOne = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      await api.post(`/workspaces/${workspaceId}/sales/suppressions`, {
        email, reason: newReason,
      });
      toast.success(`Suppressed ${email}`);
      setNewEmail("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };

  const removeOne = async (email) => {
    if (!window.confirm(`Remove ${email} from the suppression list?`)) return;
    try {
      await api.delete(`/workspaces/${workspaceId}/sales/suppressions/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };

  // CSV import — one email per line, optional second column "reason".
  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      let added = 0; let skipped = 0;
      for (const line of lines) {
        const parts = line.split(",");
        const email = (parts[0] || "").trim().toLowerCase();
        const reason = (parts[1] || "unsubscribed").trim().toLowerCase();
        if (!email || !/^\S+@\S+\.\S+$/.test(email)) { skipped++; continue; }
        try {
          await api.post(`/workspaces/${workspaceId}/sales/suppressions`, {
            email,
            reason: ["manual", "unsubscribed", "hard_bounce", "complained", "blocked"].includes(reason) ? reason : "unsubscribed",
          });
          added++;
        } catch { skipped++; }
      }
      toast.success(`Imported ${added} · skipped ${skipped}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "CSV import failed")); }
    finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="suppressions-page">
      <PageHeader
        title="Email Suppression List"
        description="Workspace blocklist. Every email here is silently skipped by every bulk campaign — protecting sender reputation and unsubscribed contacts."
        actions={
          <Button variant="outline" size="sm" className="rounded-none" onClick={load} disabled={loading}
                  data-testid="suppressions-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4">
        {/* Summary counts */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {["manual", "unsubscribed", "hard_bounce", "complained", "blocked"].map((k) => (
            <div key={k} className="border border-border p-3 space-y-1"
                 data-testid={`suppressions-count-${k}`}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k.replace("_", " ")}</div>
              <div className="text-2xl font-mono tabular-nums">{counts[k] || 0}</div>
            </div>
          ))}
        </div>

        <SuppressionSuggestPanel wsId={workspaceId} onApplied={load} />

        {/* Add + import row */}
        <div className="border border-border p-3 space-y-3" data-testid="suppressions-add-panel">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[240px]">
              <div className="text-xs font-medium mb-1">Add single email</div>
              <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                     placeholder="lead@company.com" className="rounded-none h-8"
                     onKeyDown={(e) => e.key === "Enter" && addOne()}
                     data-testid="suppressions-new-email" />
            </div>
            <div>
              <div className="text-xs font-medium mb-1">Reason</div>
              <Select value={newReason} onValueChange={setNewReason}>
                <SelectTrigger className="w-[160px] h-8 rounded-none" data-testid="suppressions-new-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">manual</SelectItem>
                  <SelectItem value="unsubscribed">unsubscribed</SelectItem>
                  <SelectItem value="hard_bounce">hard_bounce</SelectItem>
                  <SelectItem value="complained">complained</SelectItem>
                  <SelectItem value="blocked">blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="rounded-none h-8" onClick={addOne} data-testid="suppressions-add-btn">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
            <div className="border-l h-8 mx-1" />
            <input ref={fileRef} type="file" accept=".csv,text/csv"
                   className="hidden"
                   onChange={(e) => importCsv(e.target.files?.[0])} />
            <Button variant="outline" className="rounded-none h-8"
                    onClick={() => fileRef.current?.click()} disabled={importing}
                    data-testid="suppressions-import-btn">
              <Upload className="w-3 h-3 mr-1" /> {importing ? "Importing…" : "CSV import"}
            </Button>
          </div>
          <div className="text-[10px] text-muted-foreground">
            CSV format: one email per line, optional second column with reason (defaults to <code>unsubscribed</code>). Header row optional.
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 pb-1">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search email…"
                   className="pl-7 h-8 rounded-none w-[280px]"
                   onKeyDown={(e) => e.key === "Enter" && load()}
                   data-testid="suppressions-search" />
          </div>
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={load}
                  data-testid="suppressions-search-btn">Search</Button>
        </div>

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={Ban}
            title="No suppressions yet"
            description="Bad addresses will land here automatically when a campaign hard-bounces or a recipient unsubscribes."
          />
        ) : (
          <div className="border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium text-xs">Email</th>
                  <th className="px-3 py-2 font-medium text-xs w-[140px]">Reason</th>
                  <th className="px-3 py-2 font-medium text-xs w-[100px]">Source</th>
                  <th className="px-3 py-2 font-medium text-xs w-[170px]">Added</th>
                  <th className="px-3 py-2 font-medium text-xs w-[60px]"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.email} className="border-t border-border hover:bg-muted/30"
                      data-testid={`suppressions-row-${r.email}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${REASON_TONE[r.reason] || REASON_TONE.manual}`}>
                        {r.reason || "manual"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.source || "manual"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.created_at?.slice(0, 19).replace("T", " ") || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" onClick={() => removeOne(r.email)}
                              className="text-rose-600 hover:text-rose-800"
                              data-testid={`suppressions-remove-${r.email}`}
                              title="Remove from list">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// R138am · Feb 2026 — Suppression Suggest panel.
// Scans past bulk-email campaigns for emails that hard-bounced ≥ N times
// but aren't yet suppressed, and lets admins add them all in one click.
function SuppressionSuggestPanel({ wsId, onApplied }) {
  const [rows, setRows] = useState(null);
  const [minBounces, setMinBounces] = useState(2);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState({});
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/sales/suppressions/suggest?min_bounces=${minBounces}`);
      setRows(r.data?.rows || []);
      // Default: everything checked
      const next = {};
      (r.data?.rows || []).forEach((row) => { next[row.email] = true; });
      setChecked(next);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to scan campaigns")); }
    finally { setBusy(false); }
  }, [wsId, minBounces]);
  const apply = async () => {
    const emails = Object.keys(checked).filter((e) => checked[e]);
    if (emails.length === 0) return toast.error("Nothing selected");
    if (!window.confirm(`Add ${emails.length} email${emails.length === 1 ? "" : "s"} to the suppression list?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/sales/suppressions/suggest/apply`, {
        emails, reason: "hard_bounce",
      });
      toast.success(`Added ${r.data?.added || 0}, updated ${r.data?.updated || 0}`);
      setRows(null); setChecked({});
      onApplied?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setBusy(false); }
  };
  const selectedCount = Object.values(checked).filter(Boolean).length;
  return (
    <div className="border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="suppressions-suggest-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-semibold flex items-center gap-2">
          <Search className="w-4 h-4 text-primary" />
          Suggest suppressions from past bounces
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <label className="text-muted-foreground uppercase tracking-widest text-[10px]">Bounced at least</label>
          <Input type="number" min={1} max={20} value={minBounces}
                  onChange={(e) => setMinBounces(Math.max(1, Number(e.target.value) || 1))}
                  className="h-8 w-16 rounded-none text-xs font-mono"
                  data-testid="suppressions-suggest-min-bounces" />
          <span className="text-muted-foreground text-[10px] uppercase tracking-widest">time(s)</span>
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={load} disabled={busy}
                  data-testid="suppressions-suggest-scan-btn">
            {busy ? "Scanning…" : rows === null ? "Scan campaigns" : "Rescan"}
          </Button>
        </div>
      </div>
      {rows !== null && (
        rows.length === 0 ? (
          <div className="text-xs text-muted-foreground py-1">
            Nothing to add — either no repeated hard bounces, or all offenders are already on the suppression list.
          </div>
        ) : (
          <div className="border border-border bg-background">
            <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border bg-muted/40">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="checkbox"
                        checked={selectedCount === rows.length}
                        onChange={(e) => {
                          const next = {};
                          rows.forEach((r) => { next[r.email] = e.target.checked; });
                          setChecked(next);
                        }} />
                <span className="text-[10px] uppercase tracking-widest">Select all ({rows.length})</span>
              </label>
              <span className="ml-auto text-[10px] text-muted-foreground uppercase tracking-widest">
                {selectedCount} selected
              </span>
              <Button size="sm" className="rounded-none h-7 text-[11px]" onClick={apply} disabled={busy || selectedCount === 0}
                      data-testid="suppressions-suggest-apply-btn">
                Add {selectedCount} to suppression
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto text-xs">
              {rows.map((r) => (
                <label key={r.email} className="flex items-center gap-2 px-2 py-1 border-b border-border/40 hover:bg-muted/20 cursor-pointer"
                       data-testid={`suppressions-suggest-row-${r.email}`}>
                  <input type="checkbox" checked={!!checked[r.email]}
                          onChange={(e) => setChecked({ ...checked, [r.email]: e.target.checked })} />
                  <span className="font-mono text-[11px] truncate flex-1">{r.email}</span>
                  <span className="text-red-600 font-mono text-[10px]" title={`Latest: ${r.latest_reason}`}>
                    {r.bounces} bounces
                  </span>
                  <span className="text-[9px] text-muted-foreground truncate max-w-[40%]" title={r.campaigns.join(", ")}>
                    {r.campaigns[0]}{r.campaigns.length > 1 ? ` +${r.campaigns.length - 1}` : ""}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )
      )}
      {rows === null && (
        <div className="text-[11px] text-muted-foreground">
          Find emails that failed with hard-bounce codes (550, 5.1.1, &ldquo;user unknown&rdquo;…) across multiple campaigns. Not yet suppressed.
        </div>
      )}
    </div>
  );
}
